'use client';
import { createLogger } from '@svenskaspelab/nova-core/log';
import { Component, type ComponentType, type ErrorInfo, type ReactNode } from 'react';

const logger = createLogger('poolbetting.error-boundary');
const isServer = typeof window === 'undefined';

type ErrorBoundaryProps = {
  children?: ReactNode;
  Fallback?:
    | ComponentType<{
        retry: () => void;
      }>
    | ComponentType;
};

type State = {
  hasError: boolean;
};

const retry = (): void => {
  if (isServer) {
    return;
  }

  window.location.reload();
};

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, State> {
  override state: State = {
    hasError: false,
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('Uncaught error in component', { error, errorInfo, componentStack: errorInfo.componentStack });
  }

  override render(): ReactNode {
    const { Fallback, children } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      if (Fallback) {
        return <Fallback retry={retry} />;
      }

      return undefined;
    }

    return children;
  }
}

/**
 * Renders the poolbetting error boundary as a JSX-compatible function component.
 *
 * @param props Children and optional fallback component shown after a rendering error
 * @returns Error boundary component
 */
// eslint-disable-next-line react/no-multi-comp
export default function ErrorBoundary(props: ErrorBoundaryProps): ReactNode {
  return <ErrorBoundaryClass {...props} />;
}
